import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { ConfirmAlert } from "../../../../components/Alerts/Alerts";
import Button from "../../../../components/Buttons/Button";
import ChipOptional from "../../../../components/Chip/ChipOptional";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BRAND_INSIGHTS_APPLY_CONFIRM,
  BRAND_INSIGHTS_DISMISS,
  BRAND_INSIGHTS_DISMISS_ALERT_TITLE,
  BRAND_INSIGHTS_DISMISS_ALERT_DESCRIPTION,
  NEW,
} from "../../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../../utils/constants";
import { BOX_SHADOW_LITTLE } from "../../../../utils/colors";
import { getInsightTopicMeta } from "../utils/insightTopicMeta";

const POLARITY_TONES = {
  positive: {
    color: "#159457",
    background: "#dff6eb",
  },
  negative: {
    color: "#d7193f",
    background: "#ffe1e5",
  },
};

/**
 * Section already shows "Repetir" / "Evitar". Card header shows topic
 * namespace icon + label; polarity only colors that row. Actions stay
 * pinned to the bottom; height follows content / grid stretch.
 */
const BrandInsightCard = ({
  title,
  description,
  topicKey = "",
  polarity = "positive",
  canApply = false,
  isDismissing = false,
  isNew = false,
  onReview,
  onDismiss,
}) => {
  const { t } = useTranslationApp();
  const tone = POLARITY_TONES[polarity] || POLARITY_TONES.positive;
  const { Icon, labelKey } = getInsightTopicMeta(topicKey);

  const handleDismiss = async () => {
    const result = await ConfirmAlert({
      title: t(BRAND_INSIGHTS_DISMISS_ALERT_TITLE),
      text: t(BRAND_INSIGHTS_DISMISS_ALERT_DESCRIPTION),
      confirmButtonText: t(BRAND_INSIGHTS_DISMISS),
    });

    if (result.isConfirmed) {
      onDismiss();
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        height: "100%",
        borderRadius: `${BORDER_RADIUS * 2}px`,
        border: "none",
        backgroundColor: "background.paper",
        boxShadow: BOX_SHADOW_LITTLE,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          p: 2.75,
          flex: 1,
          minHeight: 0,
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
              borderRadius: "10px",
              color: tone.color,
              backgroundColor: tone.background,
              "& svg": {
                width: 18,
                height: 18,
                strokeWidth: 2.25,
              },
            }}
          >
            <Icon aria-hidden="true" />
          </Box>
          <Chip
            label={t(labelKey)}
            size="small"
            sx={{
              height: 36,
              borderRadius: "99px",
              backgroundColor: tone.background,
              color: tone.color,
              fontSize: "12px",
              fontWeight: 600,
              "& .MuiChip-label": {
                px: 1.5,
                py: 0,
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 700,
              fontSize: "0.95rem",
              lineHeight: 1.25,
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          {isNew ? (
            <ChipOptional
              size="small"
              label={t(NEW)}
              sx={{
                flexShrink: 0,
                height: 20,
                fontSize: "11px",
                fontWeight: 500,
                "& .MuiChip-label": {
                  px: 1,
                  py: 0,
                },
              }}
            />
          ) : null}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8125rem",
            lineHeight: 1.4,
            color: "text.primary",
            mb: 2,
          }}
        >
          {description}
        </Typography>
        {canApply ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mt: "auto",
            }}
          >
            <Button
              color="secondary"
              onClick={handleDismiss}
              disabled={isDismissing}
              sx={{
                minWidth: 0,
                width: "auto",
                height: 36,
                fontSize: "13px !important",
                px: 2,
              }}
            >
              {t(BRAND_INSIGHTS_DISMISS)}
            </Button>
            <Button
              color="primary"
              onClick={onReview}
              disabled={isDismissing}
              sx={{
                minWidth: 0,
                width: "auto",
                height: 36,
                fontSize: "13px !important",
                px: 2,
              }}
            >
              {t(BRAND_INSIGHTS_APPLY_CONFIRM)}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default BrandInsightCard;
